import {
    Component,
    effect,
    Inject,
    inject,
    OnInit,
    signal,
    WritableSignal
} from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import {
    CategoriesShareListComponentType,
    ContactType,
    ShareListSelectComponent
} from "../share-list-select/share-list-select.component";
import { ShareListComponent } from "../share-list/share-list.component";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from "@angular/material/dialog";
import { ModelsDialogComponent } from "../models-dialog/models-dialog.component";
import { GetUsersContactControllerType } from "@arch/presentation/controllers/get-users-contact.controller";
import { UserEntity } from "@arch/domain/entity/user.entity";
import { CompanyContactEntity } from "@arch/domain/entity/company-contact.entity";
import { FormatUtils } from "@arch/_shared/utils/format.utils";
import { AddContactDialogComponent } from "../add-contact-dialog/add-contact-dialog.component";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportEntity } from "@arch/domain/entity";
import { ContactService } from "@app/shared/services/contact.service";

@Component({
    selector: "app-share-wpp-dialog",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ShareListSelectComponent,
        ShareListComponent
    ],
    templateUrl: "./share-wpp-dialog.component.html",
    styleUrl: "./share-wpp-dialog.component.scss"
})
export class ShareWppDialogComponent implements OnInit {
    public set categories(value: CategoriesShareListComponentType[]) {
        this.categoriesSignal.set(value);
    }
    public get categories(): CategoriesShareListComponentType[] {
        return this.categoriesSignal();
    }
    public categoriesSignal: WritableSignal<
        CategoriesShareListComponentType[]
    > = signal<CategoriesShareListComponentType[]>([]);

    private readonly contactService = inject(ContactService);

    public chips: string[] = [];
    public contacts: string[] = [];
    public loading: boolean = true;
    public textValue: string = "";

    readonly dialog = inject(MatDialog);
    private readonly eventsService = inject(EventsService);
    constructor(
        public dialogRef: MatDialogRef<ShareWppDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: ShareWppDialogDataInputType
    ) {
        effect(() => {
            const categories = this.categoriesSignal();
            const contacts = categories.flatMap((category) =>
                category.contacts
                    .filter((contact) => contact.checked)
                    .map((contact) => contact.name + ";")
            );
            this.contacts = contacts;
        });
    }

    ngOnInit(): void {
        this.categories = [
            {
                _id: "empresa",
                name: "Empresa",
                contacts: []
            },
            {
                _id: "contabilidade",
                name: "Contabilidade",
                contacts: []
            }
        ];

        this.getModeledContactsByCategory();
    }

    public onChangeChips(chips: string[]): void {
        //    this.chips = chips;
    }

    private getModeledContactsByCategory() {
        this.categories[0].contacts = (
            this.data?.contacts?.companyContacts || []
        ).map((contact: CompanyContactEntity) => contact.getContactShare());

        this.categories[1].contacts = (
            this.data?.contacts?.usersContact || []
        ).map((contact: CompanyContactEntity) => contact.getContactShare());
    }

    public onOpenModelWhatsapp() {
        this.dialog.open(ModelsDialogComponent, {
            width: "100%",
            maxWidth: "666px",
            height: "100%",
            maxHeight: "507px",
            data: {
                title: "Compartilhar por WhatsApp",
                description: "Modelo da mensagem (dados fictícios)",
                image: {
                    src: "/images/print-model-whatsapp.png",
                    height: "379px",
                    width: "563px"
                }
            }
        });
    }

    public handleChips(): string[] {
        return this.chips
            .filter((c) => FormatUtils.mask("phone", c).valid)
            .map(
                (ch) =>
                    FormatUtils.mask("phone", ch, { withPrefix: true }).formated
            )
            .concat(this.contacts);
    }

    public onSubmit(): void {
        this.eventsService.send(
            `Dash.${this.data.report.type}.${this.data.report.humanSourceName}.compartilhar.whatsapp`
        );
        const contactsSelected = this.getContactsSelected();

        if (!contactsSelected.length) {
            this.eventsService.send(
                `Dash.${this.data.report.type}.${this.data.report.humanSourceName}.compartilhar-whatsapp-sem-contato`
            );
        }

        if (this.textValue.length) {
            this.chips.push(this.textValue);
        }
        this.dialogRef.close({
            chips: this.chips,
            contacts: contactsSelected
        });
    }

    public getContactsSelected() {
        const contactsSelected = this.categories
            .flatMap((category) => category.contacts)
            .filter((contact) => contact.checked);
        return contactsSelected;
    }

    public onOpenAddContact(category: CategoriesShareListComponentType) {
        this.dialog.open(AddContactDialogComponent, {
            width: "100%",
            maxWidth: "402px",
            maxHeight: "auto",
            data: {
                category: category._id === "empresa" ? "company" : "accounting",
                title: `Cadastrar Contato (${category.name})`,
                description: "Insira abaixo o contato para envio do Dash.",
                type: "whatsapp",
                companyId: this.data.companyId,
                accountingId: this.data.accountingId,
                report: this.data.report,
                handleListContacts: async () => this.refreshListContacts()
            }
        });
    }

    public onUpdateContact(contact: any): void {
        const userSession = this.contactService.get();

        const getContacts = () => {
            return contact.type === "empresa"
                ? this.data.contacts?.companyContacts
                : this.data.contacts?.usersContact;
        };

        const user = getContacts().find((c) => c.props._id === contact._id);

        this.dialog.open(AddContactDialogComponent, {
            width: "100%",
            maxWidth: "402px",
            maxHeight: "auto",
            data: {
                category: "company",
                title: `Atualizar Contato (Empresa)`,
                description: "Atualizar as informações do contato.",
                type: "whatsapp",
                action: "update",
                contact: user,
                companyId: this.data.companyId,
                accountingId: this.data.accountingId,
                handleListContacts: async () => this.refreshListContacts()
            }
        });
    }

    private async refreshListContacts(): Promise<void> {
        this.loading = true;
        const contacts = await this.data.handleListContacts();
        this.data.contacts = contacts;
        this.getModeledContactsByCategory();
        this.categories = [...this.categories];
        this.loading = false;
    }
}

type ShareWppDialogDataInputType = {
    contacts: GetUsersContactControllerType;
    companyId: string;
    accountingId: string;
    handleListContacts: () => Promise<any>;
    report: ReportEntity;
};
